import React, { useEffect, useRef, useState } from 'react';
import { useToolData } from '../hooks/useToolData';
import { useTranslation } from 'react-i18next';
import { getBootLocale } from '../../../i18n';
import { evalFormula } from '../formula';
import { IconGlyph, IsraelFlag } from '../IconGlyph';

/** Count-up easing curves the meter can be authored with. */
export type CountEasing = 'smooth' | 'linear' | 'gentle' | 'pop';
const EASINGS: Record<CountEasing, (p: number) => number> = {
  // Fast start, slow settle — the classic odometer roll-up.
  smooth: (p) => 1 - Math.pow(1 - p, 3),
  // Constant speed.
  linear: (p) => p,
  // Slow in, slow out — a calmer rise.
  gentle: (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2),
  // Slight overshoot past the value, then settle — a lively "pop".
  pop: (p) => {
    const c1 = 1.70158, c3 = c1 + 1;
    return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2);
  },
};
// CSS cubic-bezier equivalents of the JS easings above. The fill geometry is
// animated by the browser's compositor via a CSS transition (off the main
// thread) rather than a per-frame JS rAF — this is what makes the bar glide
// instead of stutter. The number counter (JS) and the fill (CSS) share the same
// duration + matching curve, so they stay visually in lockstep.
const EASING_CSS: Record<CountEasing, string> = {
  smooth: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
  linear: 'linear',
  gentle: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
  pop: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

/**
 * Fire `true` once the element scrolls into view (then stops observing). When
 * `enabled` is false, or IntersectionObserver is unavailable, it reports `true`
 * immediately so callers fall back to playing right away. Used to hold the
 * count-up at 0 until the meter is actually on screen, so the donor sees the
 * numbers rise instead of missing it above the fold.
 *
 * The public campaign renders inside a CSS `zoom`-scaled canvas, and `zoom` +
 * IntersectionObserver mis-reports the intersection ratio on iOS Safari — a
 * strict threshold could never be crossed, leaving the meter frozen at 0. So we
 * use threshold 0 (any pixel counts) AND a fallback timer that starts the
 * count-up regardless after a short delay. Correctness (never stuck at 0) wins
 * over the scroll-into-view polish in the rare case IO never fires.
 */
function useInView(ref: React.RefObject<HTMLElement>, enabled: boolean): boolean {
  const [inView, setInView] = useState(!enabled);
  useEffect(() => {
    if (!enabled) { setInView(true); return; }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    let done = false;
    const start = () => { if (!done) { done = true; setInView(true); io.disconnect(); window.clearTimeout(fallback); } };
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) start();
    }, { threshold: 0 });
    io.observe(el);
    // Safety net against the zoom/IO quirk above (and any environment where the
    // observer simply never fires) — never leave the meter stuck at 0.
    const fallback = window.setTimeout(start, 2500);
    return () => { io.disconnect(); window.clearTimeout(fallback); };
  }, [ref, enabled]);
  return inView;
}

/**
 * Tween a number toward `target` whenever it changes (and from 0 on first
 * mount). Returns the live integer to render. When `enabled` is false it passes
 * `target` straight through — no animation, byte-identical to rendering the raw
 * value. While `enabled` but `ready` is still false the value is held at its
 * start (0 on first mount) so the count-up waits for an in-view trigger. Used
 * both by the live meter (rolls up to each new poll total) and the authored
 * entrance animation (rolls 0 → value when the meter scrolls into view).
 */
function useCountUp(
  target: number,
  enabled: boolean,
  ready: boolean,
  easing: CountEasing = 'smooth',
  duration = 1100,
): number {
  const [val, setVal] = useState(enabled ? 0 : target);
  const fromRef = useRef(enabled ? 0 : target);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!enabled) { fromRef.current = target; setVal(target); return; }
    // Hold at the start value until the in-view trigger fires.
    if (!ready) { setVal(fromRef.current); return; }
    const from = fromRef.current;
    const to = target;
    if (from === to) { setVal(to); return; }
    const ease = EASINGS[easing] || EASINGS.smooth;
    let startTs: number | null = null;
    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const p = Math.min(1, (ts - startTs) / Math.max(1, duration));
      // Keep the raw float — the caller rounds for the displayed number but
      // feeds the unrounded value to the fill geometry, so the bar moves
      // continuously instead of jumping in coarse integer-percent steps.
      setVal(from + (to - from) * ease(p));
      if (p < 1) { rafRef.current = requestAnimationFrame(tick); }
      else { fromRef.current = to; setVal(to); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, enabled, ready, easing, duration]);
  return val;
}

/**
 * Renders a single rolling number in its OWN component, so the per-frame
 * count-up re-renders only this tiny span — not the whole (expensive) meter.
 * `render` turns the rounded value into the final text (currency, percent, …).
 */
const CountUpText: React.FC<{
  value: number;
  animate: boolean;
  ready: boolean;
  easing: CountEasing;
  duration: number;
  render: (n: number) => React.ReactNode;
}> = ({ value, animate, ready, easing, duration, render }) => {
  const n = useCountUp(value, animate, ready, easing, duration);
  return <>{render(Math.round(n))}</>;
};

/**
 * DynamicProgress renders the progress meter block.
 *
 * Data source is one of:
 *   - 'static'            → use `current` / `target` from props directly (legacy).
 *   - 'tool'              → fetch aggregate from a Tool's submissions via useToolData.
 *   - 'campaign'          → read `raised`/`goal` from the passed-in campaignData (builder & public).
 *   - 'campaign_recurring'→ read recurringCount from campaignData; target is the block's `target`.
 *   - 'formula'           → compute current from a formula over live campaign
 *                           metrics (raised / donationCount / recurringCount);
 *                           target is the block's `target`.
 *
 * Display modes: 'bar' (default) | 'circle' | 'number' | 'thermometer'
 */
export interface DynamicProgressProps {
  props: Record<string, any>;
  style: Record<string, any>;
  baseStyle: React.CSSProperties;
  scale?: number;
  /** Live campaign stats — passed through from the campaign builder / public page.
   *  raisedByCurrency / goalByCurrency are the multi-currency maps; when both
   *  are set AND activeCurrency picks a key in them, the block renders those
   *  per-currency numbers instead of the legacy ILS-only `raised`/`goal`. */
  campaignData?: {
    raised?: number;
    goal?: number;
    recurringCount?: number;
    /** Total number of completed donations — used by the 'formula' source. */
    donationCount?: number;
    raisedByCurrency?: Record<string, number>;
    goalByCurrency?: Record<string, number>;
  };
  /** Bilingual: in the builder this comes from the editLocale prop so the
   *  preview flips with the canvas pill. On the public page, fall back to
   *  the boot locale (which is decided once from ?lang=). When undefined we
   *  resolve at runtime — keeps the prop optional everywhere. */
  editLocale?: 'he' | 'en';
  /** Multi-currency: the donor's active currency choice on the public page.
   *  When undefined, falls back to p.currency (the legacy authored symbol).
   *  When set, picks raisedByCurrency[activeCurrency] / goalByCurrency[activeCurrency]
   *  and renders the matching symbol. */
  activeCurrency?: string;
  /** When true (live public page), the raised amount counts up with an
   *  animation and the bar/circle/thermometer fill follows it frame-by-frame
   *  instead of snapping. Off in the builder and on static pages. */
  animateValue?: boolean;
  /** True only on the rendered public page (not the builder canvas). Gates the
   *  authored count-up entrance animation (`props.countAnim`) so it plays for
   *  visitors but doesn't replay on every keystroke while editing. */
  playEntrance?: boolean;
}

const CURRENCY_SYMBOL: Record<string, string> = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };

export const DynamicProgress: React.FC<DynamicProgressProps> = ({ props: p, style: s, baseStyle, scale = 1, campaignData, editLocale, activeCurrency, animateValue, playEntrance }) => {
  const { t } = useTranslation();
  const lng = editLocale || getBootLocale();
  // Hebrew renders "X מתוך Y" (legacy wording, byte-identical to before).
  // English renders "X of Y".
  const outOf = t('public.progress.outOf', { lng }) as string;
  const isDynamic = p.dataSource === 'tool' && (p.toolId || p.toolSlug);
  const isCampaignSource = p.dataSource === 'campaign';
  const isCampaignRecurring = p.dataSource === 'campaign_recurring';
  const isFormula = p.dataSource === 'formula';

  // Build spec — only include fields that are set, to keep the cache key stable.
  const spec: Record<string, any> = { mode: p.mode || 'submissions_count' };
  if (p.fieldId) spec.fieldId = p.fieldId;
  if (p.option) spec.option = p.option;
  if (p.weights && Object.keys(p.weights).length) spec.weights = p.weights;
  if (Array.isArray(p.conditions) && p.conditions.length) spec.conditions = p.conditions;
  if (p.timeWindowDays) spec.timeWindowDays = p.timeWindowDays;

  const { data, loading } = useToolData<{ value: number }>({
    kind: 'aggregate',
    toolId: p.toolId || undefined,
    toolSlug: p.toolSlug || undefined,
    spec,
    pollSeconds: p.livePolling || 0,
    skip: !isDynamic,
  });

  // Multi-currency: when the active currency picks a key in the per-currency
  // maps, use that as the live `current`/`target`. Falls back to the legacy
  // single-number `raised`/`goal` so block previews in the builder and any
  // single-currency campaign keep their byte-identical behavior.
  const hasPerCurrencyRaised = !!(activeCurrency
    && campaignData?.raisedByCurrency
    && Object.prototype.hasOwnProperty.call(campaignData.raisedByCurrency, activeCurrency));
  const hasPerCurrencyGoal = !!(activeCurrency
    && campaignData?.goalByCurrency
    && Object.prototype.hasOwnProperty.call(campaignData.goalByCurrency, activeCurrency));

  // The live "raised" number, resolved for the active currency when available.
  // Shared by the 'campaign' source and the 'formula' source's `raised` metric.
  const raisedVal = hasPerCurrencyRaised
    ? (campaignData!.raisedByCurrency![activeCurrency!] ?? 0)
    : (campaignData?.raised ?? 0);
  // The fundraising goal, resolved for the active currency — exposed to formulas
  // as the `goal` metric (e.g. an ambassador's personal goal).
  const goalVal = hasPerCurrencyGoal
    ? (campaignData!.goalByCurrency![activeCurrency!] ?? 0)
    : (campaignData?.goal ?? 0);

  const current = isDynamic
    ? (data?.value ?? 0)
    : isFormula
      // evalFormula applies the formula's own rounding mode internally.
      ? evalFormula(p.formula, {
          raised: raisedVal,
          donationCount: campaignData?.donationCount ?? 0,
          recurringCount: campaignData?.recurringCount ?? 0,
          goal: goalVal,
        })
      : isCampaignRecurring
        ? (campaignData?.recurringCount ?? 0)
        : isCampaignSource
          ? raisedVal
          : (p.current ?? 0);
  // Campaign source uses the campaign's fundraising goal; manual target in props
  // overrides it. Recurring source has no campaign-level goal — uses the block's target.
  // `targetMode: 'formula'` computes the target from a formula over the same live
  // metrics (raised / donationCount / recurringCount) — e.g. a dynamic goal.
  const target = p.targetMode === 'formula'
    ? evalFormula(p.targetFormula, {
        raised: raisedVal,
        donationCount: campaignData?.donationCount ?? 0,
        recurringCount: campaignData?.recurringCount ?? 0,
        goal: goalVal,
      })
    : isCampaignSource
      ? (p.target && p.target > 0
          ? p.target
          : (hasPerCurrencyGoal
              ? (campaignData!.goalByCurrency![activeCurrency!] ?? 0)
              : (campaignData?.goal ?? 0)))
      : (p.target ?? 0);
  // Authored count-up entrance: numbers roll 0 → value (and the fill rises with
  // them) the first time the meter scrolls into view on the public page. Gated
  // by `playEntrance` so it doesn't replay on every keystroke in the builder.
  // 'none' (default) keeps the legacy behavior. The live meter (`animateValue`)
  // also drives the count-up — when both are off, the value renders raw.
  const countAnim: 'none' | CountEasing =
    p.countAnim && EASINGS[p.countAnim as CountEasing] ? p.countAnim : 'none';
  const entranceOn = !!playEntrance && countAnim !== 'none';
  const animate = entranceOn || !!animateValue;
  const easing: CountEasing = countAnim !== 'none' ? countAnim : 'smooth';
  const countDuration = Math.max(200, Number(p.countDuration) || 1100);

  // Hold the entrance count-up at 0 until the meter is on screen. The live
  // meter starts immediately (it has no entrance), so only gate on view for the
  // authored entrance.
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, entranceOn && !animateValue);
  const ready = animateValue ? true : inView;

  // Final (un-animated) values. The displayed numbers roll toward these via
  // isolated <CountUpText> children; the fill glides toward them via CSS.
  // Displayed percent only hits 100% once the goal is genuinely reached.
  // Plain Math.round would bump e.g. 99.6% (₪766,916 / ₪770,000) to "100%"
  // while the goal isn't actually met — capping at 99% until current >= target
  // keeps the number honest (the fill geometry still uses the exact ratio).
  const displayPct = (n: number): number => {
    if (target <= 0) return 0;
    if (n >= target) return 100;
    return Math.min(99, Math.round((n / target) * 100));
  };
  const pct = displayPct(current);
  const pctExact = target > 0 ? Math.min(100, (current / target) * 100) : 0;

  // Drive the fill with a CSS transition (compositor) instead of a per-frame JS
  // loop — that's what removes the stutter. We render the fill at 0 on the first
  // paint, then flip to the final ratio one frame later so the browser animates
  // 0 → target smoothly. `started` also re-arms whenever the meter leaves/enters
  // readiness (e.g. scrolls into view).
  const [started, setStarted] = useState(!animate);
  useEffect(() => {
    if (!animate) { setStarted(true); return; }
    if (!ready) { setStarted(false); return; }
    const id = requestAnimationFrame(() => setStarted(true));
    return () => cancelAnimationFrame(id);
  }, [animate, ready]);
  // The ratio the fill geometry actually renders: 0 until the transition is
  // armed, then the final value. When not animating it's the static value.
  const pctGeom = animate ? (started ? pctExact : 0) : pct;
  const cssEase = EASING_CSS[easing] || EASING_CSS.smooth;
  // Transition for a given fill property — the authored duration/curve while
  // animating, else the legacy 600ms ease-out (byte-identical to before).
  const fillTrans = (prop: string) => animate ? `${prop} ${countDuration}ms ${cssEase}` : `${prop} 600ms ease-out`;
  // Shared props for every rolling number on this meter — they all animate in
  // lockstep with the fill.
  const countProps = { animate, ready, easing, duration: countDuration } as const;
  // Formatters used by the rolling-number spans, given the live integer value.
  const renderPct = (n: number) => `${displayPct(n)}%`;
  const amountText = (n: number) => `${currency}${n.toLocaleString()}`;
  // Recurring count and formula results are quantities, not currency values —
  // suppress the currency symbol. When the author explicitly chose "no symbol"
  // (currency === ''), honor it everywhere — even on the public page, which
  // passes an activeCurrency that would otherwise force ₪/$. Otherwise prefer
  // the active currency symbol over the authored one (multi-currency).
  const currency = (isCampaignRecurring || isFormula || p.currency === '')
    ? ''
    : (activeCurrency
        ? (CURRENCY_SYMBOL[activeCurrency.toUpperCase()] || activeCurrency)
        : (p.currency || ''));
  // Bar fill color is separate from the wrapper background. If the user never
  // set `barColor`, fall back to the legacy `s.bgColor` so existing blocks
  // keep their current look.
  const barColor = p.barColor || s.bgColor || '#2563eb';
  const barTrack = p.barTrackColor || '#e5e7eb';
  const milestones: number[] = Array.isArray(p.milestones) ? p.milestones.filter((m: number) => m > 0 && m < 100) : [];
  // Whether to show the raised amount (the current sum). Off ⇒ only the
  // label / target / percent / bar fill show, not the number.
  const showAmount = p.showAmount !== false;

  // ── Bar layout knobs (all bar-only; other displays keep their fixed look) ──
  // Thickness of the bar track in px.
  const barThickness = Math.max(2, p.barThickness || 16);
  // Fill direction. Undefined ⇒ inherit the ambient text direction (RTL fills
  // right→left in Hebrew, LTR fills left→right in English — the legacy look).
  // 'ltr' / 'rtl' force the bar (fill, milestones, moving markers) one way
  // regardless of language, without flipping the surrounding text rows.
  const fillDir: 'ltr' | 'rtl' | undefined =
    p.fillDirection === 'ltr' ? 'ltr' : p.fillDirection === 'rtl' ? 'rtl' : undefined;
  // Where the amount row sits relative to the bar: 'top' (default) | 'bottom'.
  const amountPosition: 'top' | 'bottom' = p.amountPosition === 'bottom' ? 'bottom' : 'top';
  // How the amount (and inline target) row is laid out horizontally.
  //   'space-between' (default) → amount one side, target the other
  //   'flex-start' | 'center' | 'flex-end' → grouped to that edge / center
  const amountAlign: string = p.amountAlign || 'space-between';
  const amountFontSize = (p.amountFontSize || 14) * scale;
  const amountFontFamily: string | undefined = p.amountFont || undefined;

  // Target can be split off from the amount onto its own row, with its own
  // position / alignment / size / font. 'inline' (default) keeps the legacy
  // single row (amount on one side, target on the other).
  const targetPosition: 'inline' | 'top' | 'bottom' =
    p.targetPosition === 'top' ? 'top' : p.targetPosition === 'bottom' ? 'bottom' : 'inline';
  const targetAlign: string = p.targetAlign || 'center';
  const targetFontSize = (p.targetFontSize || p.amountFontSize || 14) * scale;
  const targetFontFamily: string | undefined = p.targetFont || amountFontFamily;
  const showTargetText = p.showTarget !== false && target > 0;

  // Percentage placement:
  //   'below'    → centered under the bar (default / legacy)
  //   'inBar'    → rides inside the fill, moving with progress (legacy pctInsideBar)
  //   'nearIcon' → next to the moving thumb icon
  //   'inIcon'   → centered on top of the moving thumb icon
  const pctPosition: 'below' | 'inBar' | 'nearIcon' | 'inIcon' =
    p.pctPosition || (p.pctInsideBar ? 'inBar' : 'below');
  const pctFontSize = (p.pctFontSize || 14) * scale;
  const pctFontFamily: string | undefined = p.pctFont || undefined;
  // White reads best when painted on top of the fill or the icon; otherwise
  // fall back to the bar color.
  const pctColor = p.pctColor || ((pctPosition === 'inBar' || pctPosition === 'inIcon') ? '#ffffff' : barColor);
  const wantPct = !!p.showPercent && target > 0;

  const display = p.display || 'bar';
  // Respect the user's bg settings: if they explicitly chose a bg color, honor it;
  // if they ticked "transparent" (s.bgTransparent) force-clear both bg layers.
  // `s.bgColor` is also used as the bar fill color — we fall back to `barColor`
  // if the user wants to keep the bar color without painting the wrapper.
  const wrapStyle: React.CSSProperties = s.bgTransparent
    ? { ...baseStyle, backgroundColor: 'transparent', backgroundImage: 'none' }
    : { ...baseStyle };

  if (display === 'number') {
    return (
      <div ref={rootRef} style={wrapStyle}>
        {p.label && <div style={{ fontSize: 14, color: '#6b7280', textAlign: 'center' }}>{p.label}</div>}
        {showAmount && (
          <div style={{ fontSize: 48 * scale, fontWeight: 'bold', color: barColor, textAlign: 'center', lineHeight: 1.1 }}>
            {loading ? '…' : <CountUpText {...countProps} value={current} render={amountText} />}
          </div>
        )}
        {p.showTarget !== false && target > 0 && (
          <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center' }}>{outOf} {currency}{target.toLocaleString()}</div>
        )}
      </div>
    );
  }

  if (display === 'circle') {
    const size = 140 * scale;
    const strokeW = 14;
    const r = (size - strokeW) / 2;
    const c = 2 * Math.PI * r;
    const offset = c * (1 - pctGeom / 100);
    return (
      <div ref={rootRef} style={{ ...wrapStyle, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {p.label && <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{p.label}</div>}
        <svg width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={barTrack} strokeWidth={strokeW} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={barColor} strokeWidth={strokeW}
            strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: fillTrans('stroke-dashoffset') }} />
          <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fontSize={28} fontWeight="bold" fill={barColor}>
            {target > 0
              ? <CountUpText {...countProps} value={current} render={renderPct} />
              : (showAmount ? <CountUpText {...countProps} value={current} render={(n) => n.toLocaleString()} /> : '')}
          </text>
        </svg>
        {p.showTarget !== false && target > 0 && (
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
            {showAmount && <CountUpText {...countProps} value={current} render={(n) => `${amountText(n)} / `} />}{currency}{target.toLocaleString()}
          </div>
        )}
      </div>
    );
  }

  if (display === 'thermometer') {
    const h = 160 * scale;
    return (
      <div ref={rootRef} style={{ ...wrapStyle, display: 'flex', justifyContent: 'center' }}>
        <div style={{ position: 'relative', width: 40, height: h }}>
          <div style={{ position: 'absolute', inset: 0, background: barTrack, borderRadius: 20 }} />
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: `${pctGeom}%`, background: barColor, borderRadius: 20,
            transition: fillTrans('height'),
          }} />
          <div style={{
            position: 'absolute', bottom: -12, left: '50%', transform: 'translateX(-50%)',
            width: 52, height: 52, borderRadius: '50%', background: barColor,
          }} />
        </div>
        <div style={{ marginRight: 16, alignSelf: 'center', fontWeight: 'bold', color: barColor, fontSize: 20 }}>
          {target > 0
            ? <CountUpText {...countProps} value={current} render={renderPct} />
            : (showAmount ? <CountUpText {...countProps} value={current} render={amountText} /> : '')}
        </div>
      </div>
    );
  }

  // default: 'bar'
  // Optional icon marker that rides the fill edge and moves with progress
  // (a "slider handle" look). Sits in a taller row so it isn't clipped.
  const showThumb = !!p.thumbIcon && !!p.thumbIconName;
  const thumbSize = (p.thumbIconSize || 28) * scale;
  const thumbColor = p.thumbIconColor || barColor;

  // Resolve where the % actually renders. 'nearIcon' / 'inIcon' need the thumb;
  // if it's off they fall back to 'below' so the % never silently vanishes.
  const iconPct = wantPct && (pctPosition === 'nearIcon' || pctPosition === 'inIcon') && showThumb;
  const inBarPct = wantPct && pctPosition === 'inBar';
  const belowPct = wantPct && (pctPosition === 'below'
    || ((pctPosition === 'nearIcon' || pctPosition === 'inIcon') && !showThumb));

  // ── Amount / target rows (above and/or below the bar) ──────────────────────
  const amountSpan = (
    <span>{p.label || ''}{p.label && showAmount ? ': ' : ''}{showAmount && <CountUpText {...countProps} value={current} render={amountText} />}</span>
  );
  // Target carries its OWN font size / family so it can be smaller than the
  // amount even on the shared inline row (e.g. a big "₪60,000" with a small
  // "מתוך ₪100,000" beside it). In the separate-row layouts this matches the
  // row's own size, so nothing changes there.
  const targetSpan = (
    <span style={{ fontSize: targetFontSize, fontFamily: targetFontFamily }}>{outOf} {currency}{target.toLocaleString()}</span>
  );
  const mkRow = (key: string, children: React.ReactNode, align: string, place: 'above' | 'below', fs: number, ff?: string) => (
    <div key={key} style={{
      display: 'flex', justifyContent: align, alignItems: 'baseline', gap: 10,
      marginBottom: place === 'above' ? 6 : 0,
      marginTop: place === 'below' ? 6 : 0,
      fontSize: fs, fontWeight: 'bold', fontFamily: ff,
    }}>{children}</div>
  );
  const aboveRows: React.ReactNode[] = [];
  const belowRows: React.ReactNode[] = [];
  if (targetPosition === 'inline') {
    // Legacy single row: amount + target together, on the amount's side.
    (amountPosition === 'top' ? aboveRows : belowRows).push(
      mkRow('amt', <>{amountSpan}{showTargetText && targetSpan}</>, amountAlign, amountPosition === 'top' ? 'above' : 'below', amountFontSize, amountFontFamily)
    );
  } else {
    // Amount and target on separate rows, each with its own position/align/font.
    (amountPosition === 'top' ? aboveRows : belowRows).push(
      mkRow('amt', amountSpan, amountAlign, amountPosition === 'top' ? 'above' : 'below', amountFontSize, amountFontFamily)
    );
    if (showTargetText) {
      (targetPosition === 'top' ? aboveRows : belowRows).push(
        mkRow('tgt', targetSpan, targetAlign, targetPosition === 'top' ? 'above' : 'below', targetFontSize, targetFontFamily)
      );
    }
  }

  // Row height has to clear whatever rides on top of the track (thumb icon, an
  // inside-the-bar percentage, or an icon-anchored percentage).
  const rowHeight = Math.max(
    barThickness,
    showThumb ? thumbSize : 0,
    (inBarPct || iconPct) ? pctFontSize * 1.4 : 0,
  );
  const barTrackEl = (
    <div style={{ position: 'relative', width: '100%', height: rowHeight, display: 'flex', alignItems: 'center', direction: fillDir }}>
      <div style={{ position: 'relative', width: '100%', height: barThickness, background: barTrack, borderRadius: 999, overflow: 'hidden' }}>
        <div style={{
          width: `${pctGeom}%`, height: '100%', background: barColor, borderRadius: 999,
          transition: fillTrans('width'),
        }} />
        {milestones.map(m => (
          <div key={m} style={{
            position: 'absolute', top: 0, bottom: 0, insetInlineStart: `${m}%`,
            width: 2, background: 'rgba(0,0,0,0.3)',
          }} />
        ))}
      </div>
      {/* Markers ride the fill's tip and move with progress. Each is wrapped in
          a zero-width flex box anchored at the tip via the logical inline-start,
          then centered with justify/align — NOT a translate. A translateX would
          only center correctly in LTR; in RTL it pushes the marker a full width
          off the tip. The zero-width box has no inline extent, so its center is
          the tip itself in both directions. */}
      {inBarPct && (
        // The label rides INSIDE the filled region, hugging the tip — not
        // centered on the tip itself. Centering on the tip put half the text
        // over the lighter track, so the leading digit washed out. We span a
        // box of width pct% from the fill's start and flex-end the label with a
        // little inset, so the whole "NN%" sits on the fill in both directions
        // (logical props respect the bar's `direction: fillDir`).
        <div style={{
          position: 'absolute', insetInlineStart: 0, top: 0, bottom: 0, width: `${pctGeom}%`,
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          paddingInlineEnd: 8, boxSizing: 'border-box',
          transition: fillTrans('width'), pointerEvents: 'none', overflow: 'hidden',
        }}>
          <span style={{ color: pctColor, fontWeight: 'bold', fontSize: pctFontSize, fontFamily: pctFontFamily, whiteSpace: 'nowrap' }}><CountUpText {...countProps} value={current} render={renderPct} /></span>
        </div>
      )}
      {showThumb && (
        <div style={{
          position: 'absolute', insetInlineStart: `${pctGeom}%`, top: 0, bottom: 0, width: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: fillTrans('inset-inline-start'), pointerEvents: 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {iconPct && pctPosition === 'nearIcon' && (
              <span style={{ lineHeight: 1, color: pctColor, fontWeight: 'bold', fontSize: pctFontSize, fontFamily: pctFontFamily, whiteSpace: 'nowrap' }}><CountUpText {...countProps} value={current} render={renderPct} /></span>
            )}
            <span style={{ position: 'relative', lineHeight: 0, display: 'inline-flex' }}>
              {p.thumbIconSource === 'flag'
                ? <IsraelFlag size={thumbSize} />
                : <IconGlyph source={p.thumbIconSource || 'material'} name={p.thumbIconName} color={thumbColor} fill weight={600} size={`${thumbSize}px`} outline={p.thumbIconOutline ? (p.thumbIconOutlineWidth ? `${p.thumbIconOutlineWidth}px #000` : true) : false} />}
              {iconPct && pctPosition === 'inIcon' && (
                <span style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: pctColor, fontWeight: 'bold', fontSize: pctFontSize, fontFamily: pctFontFamily, whiteSpace: 'nowrap', lineHeight: 1,
                }}><CountUpText {...countProps} value={current} render={renderPct} /></span>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
  // Below-the-bar percentage (used when not riding inside the fill / on the icon).
  const percentText = belowPct && (
    <p style={{ textAlign: 'center', marginTop: 4, color: pctColor, fontWeight: 'bold', fontSize: pctFontSize, fontFamily: pctFontFamily }}><CountUpText {...countProps} value={current} render={renderPct} /></p>
  );
  return (
    <div ref={rootRef} style={wrapStyle}>
      {aboveRows}
      {barTrackEl}
      {belowRows}
      {percentText}
    </div>
  );
};

export default DynamicProgress;
